
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useOptimizedAuth } from "@/hooks/useOptimizedAuth";
import { useOptimizedCart } from "@/contexts/OptimizedCartContext";
import { useMobileMenuAccessibility } from "@/hooks/useMobileMenuAccessibility";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { motion, AnimatePresence } from "framer-motion";
import { Menu } from "lucide-react";
import MobileMenuNav from "./mobile-menu/MobileMenuNav";
import MobileMenuShopping from "./mobile-menu/MobileMenuShopping";
import MobileMenuAccount from "./mobile-menu/MobileMenuAccount";
import MobileMenuUser from "./mobile-menu/MobileMenuUser";
import MobileMenuAuth from "./mobile-menu/MobileMenuAuth";
import MobileMenuQuickActions from "./mobile-menu/MobileMenuQuickActions";
import { hapticFeedback, lockScrolling, unlockScrolling } from "@/utils/mobileUtils";

/**
 * MobileMenu Component
 * 
 * Professional mobile navigation menu with desktop-grade polish.
 * 
 * Features:
 * - Phase 1: Enhanced visual design with blur effects, gradients, and proper spacing
 * - Phase 2: Optimized navigation with larger touch targets (56px) and smooth animations
 * - Phase 3: Enhanced user section with avatar, role badges, and loading states
 * - Phase 4: Advanced animations, scroll locking, haptic feedback, and accessibility
 * - Phase 5: Auto-close on route change and smooth navigation transitions
 * - Phase 6: Comprehensive documentation and TypeScript interfaces
 * 
 * Accessibility:
 * - ARIA labels and roles throughout
 * - Focus trap management
 * - Screen reader announcements
 * - Keyboard navigation support (Escape to close)
 * - Reduced motion support
 * 
 * Mobile Optimizations:
 * - Safe area insets for notched devices
 * - Overscroll prevention
 * - Body scroll locking when open
 * - Haptic feedback on interactions
 * - Touch-optimized button sizes
 * 
 * @returns {JSX.Element} Mobile navigation menu
 */
const MobileMenu = () => {
  const { user, signOut } = useOptimizedAuth();
  const { itemCount } = useOptimizedCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  
  // Phase 4.3: Accessibility support
  useMobileMenuAccessibility(isOpen);

  // Prevent body scroll when menu is open (Phase 4.2)
  useEffect(() => {
    if (isOpen) {
      lockScrolling();
      hapticFeedback('medium');
    } else {
      unlockScrolling();
    }
    
    return () => {
      unlockScrolling();
    };
  }, [isOpen]);

  // Auto-close menu on route change (Phase 4.1)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleLinkClick = (path: string) => {
    hapticFeedback('light');
    setIsOpen(false);
    // Small delay for smooth menu close before navigation
    setTimeout(() => navigate(path), 150);
  };

  const handleSignOut = async () => {
    hapticFeedback('medium');
    setIsOpen(false);
    await signOut();
    navigate('/');
  };

  const handleWishlistClick = () => {
    setIsOpen(false);
    if (!user) {
      navigate('/auth');
      return;
    }
    navigate('/wishlist');
  };

  const handleOrdersClick = () => {
    setIsOpen(false);
    if (!user) {
      navigate('/auth');
      return;
    }
    navigate('/orders');
  };

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.04,
        delayChildren: 0.05,
      },
    },
    exit: {
      transition: {
        staggerChildren: 0.02,
        staggerDirection: -1,
      },
    },
  };
  
  return (
    <div className="md:hidden">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Open navigation menu"
            onClick={() => hapticFeedback('light')}
          >
            <Menu className="h-6 w-6" />
            <span className="sr-only">Open menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent 
          side="left" 
          className="w-[320px] sm:w-[360px] p-0 flex flex-col bg-background/98 backdrop-blur-2xl border-border/30 shadow-2xl safe-area-inset"
          aria-label="Mobile navigation menu"
        >
          <SheetHeader className="relative p-4 border-b border-border/30 bg-gradient-to-br from-primary/5 to-accent/5 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <SheetTitle className="text-2xl font-bold text-foreground tracking-tight">
                Menu
              </SheetTitle>
            </motion.div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto overscroll-contain" role="navigation" aria-label="Main navigation">
            {/* Quick Actions Section */}
            <AnimatePresence mode="wait">
              {isOpen && (
                <motion.div
                  className="p-3"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <MobileMenuQuickActions 
                    user={user}
                    itemCount={itemCount}
                    handleLinkClick={handleLinkClick}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="h-px bg-gradient-to-r from-transparent via-border/40 to-transparent my-2" />

            {/* Navigation Section */}
            <motion.div
              className="p-3 space-y-1"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="mb-3 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-primary" />
                <h3 className="text-xs font-bold text-foreground/80 uppercase tracking-wider">Navigation</h3>
                <div className="flex-1 h-px bg-gradient-to-r from-border/20 to-transparent" />
              </div>
              <MobileMenuNav handleLinkClick={handleLinkClick} />
            </motion.div>
            
            <div className="h-px bg-gradient-to-r from-transparent via-border/40 to-transparent my-2" />

            {/* Shopping Section */}
            <motion.div 
              className="p-3 space-y-1"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="mb-3 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-primary" />
                <h3 className="text-xs font-bold text-foreground/80 uppercase tracking-wider">Shopping</h3>
                <div className="flex-1 h-px bg-gradient-to-r from-border/20 to-transparent" />
              </div>
              <MobileMenuShopping 
                itemCount={itemCount}
                handleLinkClick={handleLinkClick}
                handleWishlistClick={handleWishlistClick}
                handleOrdersClick={handleOrdersClick}
              />
            </motion.div>
            
            {user && (
              <>
                <div className="h-px bg-gradient-to-r from-transparent via-border/40 to-transparent my-2" />
                <motion.div
                  className="p-3 space-y-1"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <div className="mb-3 flex items-center gap-2">
                    <div className="h-1 w-1 rounded-full bg-primary" />
                    <h3 className="text-xs font-bold text-foreground/80 uppercase tracking-wider">Account</h3>
                    <div className="flex-1 h-px bg-gradient-to-r from-border/20 to-transparent" />
                  </div>
                  <MobileMenuAccount user={user} handleLinkClick={handleLinkClick} />
                </motion.div>
              </>
            )}
          </div>

          <div className="mt-auto p-3 border-t border-border/30 bg-gradient-to-br from-primary/5 via-background/95 to-accent/5 backdrop-blur-md shadow-[0_-4px_16px_rgba(0,0,0,0.1)]">
              {user ? (
                 <MobileMenuUser user={user} handleSignOut={handleSignOut} />
              ) : (
                <MobileMenuAuth handleLinkClick={handleLinkClick} />
              )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MobileMenu;
